import { useMemo } from 'react'

type ContrastTheme = 'dark' | 'light'

/**
 * Custom hook that determines the appropriate contrast theme (dark or light)
 * based on the background color of a given DOM element.
 *
 * Uses WCAG-compliant luminance calculation with gamma correction to determine
 * whether dark or light text would provide better contrast against the background.
 *
 * @param element - The DOM element to analyze for background color. If null/undefined, returns default.
 * @returns 'dark' for light backgrounds (use dark text), 'light' for dark backgrounds (use light text)
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * const theme = useContrastTheme(ref.current);
 *
 * return (
 *   <div ref={ref} style={{ backgroundColor: 'blue' }}>
 *     <span style={{ color: theme === 'dark' ? 'black' : 'white' }}>
 *       Text with proper contrast
 *     </span>
 *   </div>
 * );
 * ```
 */
export const useContrastTheme = (element?: Element | null): ContrastTheme => {
  return useMemo(() => {
    const defaultContrastColor: ContrastTheme = 'dark'

    if (!element || typeof window === 'undefined') {
      return defaultContrastColor
    }

    try {
      const style = window.getComputedStyle(element)
      const bgColor = style.backgroundColor

      // Handle transparent or rgba with alpha 0
      if (bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
        // Walk up the DOM tree to find a parent with background
        let parent = element.parentElement
        while (parent) {
          const parentBg = window.getComputedStyle(parent).backgroundColor
          if (parentBg !== 'transparent' && parentBg !== 'rgba(0, 0, 0, 0)') {
            return getContrastFromColor(parentBg, defaultContrastColor)
          }
          parent = parent.parentElement
        }
        // Default to body background or white
        const bodyBg = window.getComputedStyle(document.body).backgroundColor
        return bodyBg !== 'transparent'
          ? getContrastFromColor(bodyBg, defaultContrastColor)
          : 'dark'
      }

      return getContrastFromColor(bgColor, defaultContrastColor)
    } catch (error) {
      return defaultContrastColor
    }
  }, [element])
}

function getContrastFromColor(color: string, fallback: ContrastTheme): ContrastTheme {
  // Extract RGB values (matches rgb(...) or rgba(...))
  const rgb = color.match(/\d+/g)?.map(Number)

  if (!rgb || rgb.length < 3) {
    return fallback
  }

  const [r, g, b] = rgb

  // Convert to linear RGB for more accurate luminance calculation
  const toLinear = (channel: number): number => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }

  // Calculate relative luminance using sRGB formula with gamma correction
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)

  // WCAG contrast threshold (0.179 is approximately equivalent to #777)
  return luminance > 0.179 ? 'dark' : 'light'
}
